/**
 * Overlay a school shop's REAL prices on product cards shown outside the shop.
 *
 * Landing / listing / widget cards come from the default collection or from bare
 * products, so they carry default prices; class and shop price overrides live on the
 * school shop's own variations. `prices` is the light list of
 * GET /api/schools/{brand}/{school}/card-prices (hooks/useShopCardPrices):
 *   { applied, variations: [{id, productId, name, slug, price, colors}], packages: [{id, slug, name, price}] }
 *
 * A card says what it is through `card.match` (the same object its click target uses:
 * {type: 'product'|'package', id, productId, slug, name}); a card without one (static
 * demo cards) is matched by its title. Matching mirrors services/shopDestination.js,
 * so the price shown is the price of the page the click lands on.
 */

import { discountCardFields } from './discountBadge';

// Same folding as shopDestination: case / punctuation-insensitive ("STANDARD - HOODIE" = "Standard – Hoodie").
const norm = (s) => String(s ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

/** The shop's variation for a product card, or null when the shop does not sell it. */
export function matchVariation(variations, match) {
  const list = Array.isArray(variations) ? variations : [];
  const name = norm(match?.name);
  if (match?.productId != null) {
    const ofProduct = list.filter((v) => String(v.productId ?? v.product_id) === String(match.productId));
    return ofProduct.find((v) => match.id != null && String(v.id) === String(match.id))
      ?? ofProduct.find((v) => name && norm(v.name) === name)
      ?? ofProduct[0]
      ?? null;
  }
  return (name && list.find((v) => norm(v.name) === name)) || null;
}

/** The shop's package for a package card, or null. */
export function matchPackage(packages, match) {
  const list = Array.isArray(packages) ? packages : [];
  const name = norm(match?.name);
  return list.find((p) => (match?.id != null && String(p.id) === String(match.id)) || (match?.slug && p.slug === match.slug))
    ?? (name ? list.find((p) => norm(p.name) === name) : null)
    // A card title that EXTENDS the package name, or the reverse ("Standard Pakke 1 (Fruit
    // of the Loom)" = "Standard Pakke 1") — whole words only, so "Pakke 1" never takes "Pakke 10".
    ?? (name ? list.find((p) => { const n = norm(p.name); return n && (name.startsWith(`${n} `) || n.startsWith(`${name} `)); }) : null)
    ?? null;
}

/**
 * Cards with the shop's prices; cards the shop does not sell are dropped.
 * Returns the input untouched while `prices.applied` is not true.
 */
export function applyShopPrices(cards, prices, { feSlug } = {}) {
  if (!Array.isArray(cards) || !prices?.applied) return cards;
  return cards.reduce((out, card) => {
    const match = card.match ?? { name: card.title };
    const hit = match.type === 'package'
      ? matchPackage(prices.packages, match)
      : match.type === 'product'
        ? matchVariation(prices.variations, match)
        // Untyped (demo) card: its title names a product or a package.
        : (matchVariation(prices.variations, match) ?? matchPackage(prices.packages, match));
    // …and the shop's COLOURS of that variation (an identified visitor sees what their
    // shop sells, not the default collection's range). Older API answers carry none.
    // …and the discount state of THAT price: a shop / class override hides a listing's badge.
    if (hit) out.push({ ...card, price: hit.price, ...discountCardFields(hit, feSlug), ...(Array.isArray(hit.colors) ? { colors: hit.colors } : {}) });
    return out;
  }, []);
}
